import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  Phone, 
  WhatsappLogo, 
  MapPin, 
  InstagramLogo,
  Clock,
  ChatCircle,
  PaperPlaneTilt,
  User,
  SpinnerGap,
  CheckCircle,
  ArrowRight
} from '@phosphor-icons/react';
import { PrimarySectionBackdrop } from '../components/PrimarySectionBackdrop';
import { Seo } from '../components/Seo';
import { API_BASE } from '../config';
import { CONTACT } from '../constants/contact';
import { SEO } from '../constants/seoCopy';

const contactMethods = [
  {
    icon: Phone,
    title: 'Telefon',
    value: CONTACT.phoneDisplay,
    href: null,
    description: 'Pozovite nas direktno',
    color: 'bg-primary',
  },
  {
    icon: WhatsappLogo,
    title: 'WhatsApp',
    value: 'Pišite nam',
    href: CONTACT.whatsapp,
    description: 'Brza komunikacija',
    color: 'bg-green-500',
    external: true,
  },
  {
    icon: ChatCircle,
    title: 'Viber',
    value: 'Pišite nam',
    href: CONTACT.viber,
    description: 'Alternativna opcija',
    color: 'bg-purple-600',
  },
  {
    icon: InstagramLogo,
    title: 'Instagram',
    value: CONTACT.instagramHandle,
    href: CONTACT.instagram,
    description: 'Pratite nas',
    color: 'bg-gradient-to-br from-purple-600 to-pink-500',
    external: true,
  },
];

const SERVICE_OPTIONS = [
  'Klimatizacija',
  'Grejanje',
  'Ventilacija',
  'VIK (vodovod i kanalizacija)',
  'Hidrantske instalacije',
  'Sprinkler sistemi',
  'Odimljavanje garaža',
  'Ostalo',
];

/** state + query (query ostaje u URL-u ako history state nestane pri hash navigaciji). */
function readPrefillFromLocation(loc) {
  const st = loc.state && typeof loc.state === 'object' ? loc.state : {};
  const params = new URLSearchParams(loc.search);
  const service =
    typeof st.prefillService === 'string'
      ? st.prefillService
      : (params.get('service') || '');
  const message =
    typeof st.prefillMessage === 'string'
      ? st.prefillMessage
      : (params.get('message') || '');
  return { service, message };
}

export default function KontaktPage() {
  const location = useLocation();
  const [formData, setFormData] = useState(() => {
    const { service, message } = readPrefillFromLocation(location);
    return {
      name: '',
      phone: '',
      service,
      message,
    };
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const { service, message } = readPrefillFromLocation(location);
    if (service || message) {
      setFormData((prev) => ({
        ...prev,
        ...(service ? { service } : {}),
        ...(message ? { message } : {}),
      }));
    }
  }, [location]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const controller = new AbortController();
    const timeoutMs = 35_000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    fetch(`${API_BASE}/api/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      signal: controller.signal,
      body: JSON.stringify({
        name: formData.name,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const detail =
            typeof data.note === 'string' && data.note.trim()
              ? data.note.trim()
              : `Greška ${res.status}`;
          throw new Error(detail);
        }
        return data;
      })
      .then(() => {
        setSubmitted(true);
        setFormData({ name: '', phone: '', service: '', message: '' });
      })
      .catch((err) => {
        console.error(err);
        if (err.name === 'AbortError') {
          alert('Zahtev je predugo trajao. Proverite SMTP / mrežu na serveru ili pokušajte kasnije.');
        } else {
          alert(err.message || 'Greška pri slanju upita. Pokušajte ponovo.');
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setIsSubmitting(false);
        setTimeout(() => setSubmitted(false), 3000);
      });
  };

  return (
    <div data-testid="kontakt-page" className="pt-20">
      <Seo {...SEO.kontakt} />
      {/* Hero */}
      <section className="relative py-24 bg-primary overflow-hidden" data-testid="kontakt-header">
        <PrimarySectionBackdrop />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-cool font-bold uppercase tracking-[0.2em] text-sm mb-4">
              Kontakt
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
              Javite nam se
            </h1>
            <p className="text-white/70 text-lg">
              Kontaktirajte nas putem telefona, WhatsApp-a, Viber-a ili forme za brzu i profesionalnu uslugu.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-white" data-testid="contact-methods">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, i) => {
              const cardClass =
                'group bg-surface p-6 rounded-2xl transition-all duration-300 card-hover ' +
                (method.href ? 'hover:shadow-xl' : 'cursor-default');
              const content = (
                <>
                  <div
                    className={`w-14 h-14 ${method.color} rounded-xl flex items-center justify-center mb-4 text-white`}
                  >
                    <method.icon size={28} weight="fill" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-primary mb-1">{method.title}</h3>
                  <p className="text-primary font-medium mb-1">{method.value}</p>
                  <p className="text-muted text-sm">{method.description}</p>
                </>
              );
              if (!method.href) {
                return (
                  <motion.div
                    key={i}
                    className={cardClass}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    data-testid={`contact-method-${method.title.toLowerCase()}`}
                  >
                    {content}
                  </motion.div>
                );
              }
              return (
                <motion.a
                  key={i}
                  href={method.href}
                  target={method.external ? '_blank' : undefined}
                  rel={method.external ? 'noopener noreferrer' : undefined}
                  className={cardClass}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`contact-method-${method.title.toLowerCase()}`}
                >
                  {content}
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Info — id za hash /kontakt#kontakt-form */}
      <section
        id="kontakt-form"
        className="relative scroll-mt-24 overflow-hidden bg-primary py-24"
        data-testid="contact-form-section"
      >
        <PrimarySectionBackdrop />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-black text-white mb-8">
                Pošaljite upit
              </h2>

              <form 
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl p-8 shadow-2xl"
                data-testid="contact-form"
              >
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Vaše ime</label>
                    <div className="relative">
                      <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Unesite vaše ime"
                        className="w-full pl-12 pr-4 py-3 bg-surface rounded-xl border border-primary/10 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                        required
                        data-testid="contact-form-name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Broj telefona</label>
                    <div className="relative">
                      <Phone size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+381 6X XXX XXXX"
                        className="w-full pl-12 pr-4 py-3 bg-surface rounded-xl border border-primary/10 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                        required
                        data-testid="contact-form-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Vrsta usluge</label>
                    <select
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full px-4 py-3 bg-surface rounded-xl border border-primary/10 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                      data-testid="contact-form-service"
                    >
                      <option value="">Izaberite uslugu</option>
                      {SERVICE_OPTIONS.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Poruka</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Opišite šta vam treba..."
                      rows={4}
                      className="w-full px-4 py-3 bg-surface rounded-xl border border-primary/10 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all resize-none"
                      required
                      data-testid="contact-form-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-accent hover:bg-accent/90 text-white py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all duration-300 disabled:opacity-70"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <SpinnerGap size={24} className="animate-spin" />
                        Slanje...
                      </>
                    ) : submitted ? (
                      <>
                        <CheckCircle size={24} weight="fill" />
                        Poslato!
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={24} weight="fill" />
                        Pošalji upit
                      </>
                    )}
                  </button>
                </div>

                <p className="text-muted text-xs text-center mt-4">
                  Kontaktiraćemo vas nazad u najkraćem roku.
                </p>
              </form>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-black text-white mb-8">
                Informacije
              </h2>

              <div className="space-y-6 mb-10">
                <div className="flex gap-4 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={28} weight="duotone" className="text-accent" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary mb-1">Adresa</h4>
                    <address className="not-italic text-muted" data-testid="contact-address">
                      {CONTACT.address}
                    </address>
                  </div>
                </div>

                <div className="flex gap-4 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={28} weight="duotone" className="text-accent" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary mb-1">Radno vreme</h4>
                    <p className="text-muted" data-testid="contact-hours">
                      Ponedeljak - Subota: 08:00 - 17:00<br />
                      Nedelja: Po dogovoru
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={28} weight="duotone" className="text-accent" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary mb-1">Zona pokrivanja</h4>
                    <p className="text-muted">
                      Srbija,Crna Gora, BiH, Hrvatska
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg" data-testid="map-container">
                <div className="aspect-video bg-surface flex flex-col items-center justify-center p-8">
                  <MapPin size={48} weight="duotone" className="text-muted mb-4" />
                  <p className="text-muted text-center mb-4 max-w-md">
                    {CONTACT.address}
                  </p>
                  <a
                    href={CONTACT.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-accent font-medium hover:underline"
                    data-testid="map-link"
                  >
                    Otvori u Google Maps
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="py-16 bg-heat" data-testid="emergency-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-heading text-2xl sm:text-3xl font-black text-white mb-4">
              Hitna intervencija?
            </h3>
            <p className="text-white/90 mb-6">
              Za hitne slučajeve, pozovite nas odmah - tu smo za vas 24/7!
            </p>
            <span
              className="inline-flex items-center gap-3 bg-white text-heat px-10 py-5 rounded-xl font-bold text-xl cursor-default select-none"
              data-testid="emergency-call-display"
            >
              <Phone size={28} weight="fill" aria-hidden />
              {CONTACT.phoneDisplay}
            </span>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
